import { useEffect, useState } from "react";
import { BILLING_URL, api } from "@/lib/api";

/**
 * Ringkasan pemakaian generate gambar.
 *
 * Angka ini penting karena biayanya ditagih ke project Google milik pengguna,
 * jadi mereka berhak tahu sudah berapa yang terpakai tanpa harus menebak dari
 * tagihan. Aplikasi tidak pernah berani mengklaim ini sama persis dengan
 * tagihan Google, jadi sifatnya perkiraan selalu dinyatakan terbuka.
 */
export default function UsagePanel({ compact = false }) {
  const [usage, setUsage] = useState(null);
  const [state, setState] = useState("loading");

  const load = async () => {
    setState("loading");
    try {
      const { data } = await api.get("/usage");
      setUsage(data);
      setState("ready");
    } catch {
      setState("error");
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (state === "loading") {
    return (
      <div className="border border-zinc-200 bg-white p-4 text-[12px] text-zinc-500">
        Memuat pemakaian…
      </div>
    );
  }

  if (state === "error" || !usage) {
    return (
      <div className="border border-zinc-200 bg-white p-4">
        <p className="text-[12px] text-zinc-600">Pemakaian belum bisa dimuat.</p>
        <button
          type="button"
          onClick={load}
          className="mt-2 border border-zinc-300 px-3 py-1 text-[12px] hover:border-zinc-900"
        >
          Coba lagi
        </button>
      </div>
    );
  }

  const money = (n) => `$${Number(n || 0).toFixed(2)}`;

  return (
    <div className="border border-zinc-200 bg-white" data-testid="usage-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-zinc-200 p-4">
        <div>
          <h3 className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            Pemakaian generate gambar
          </h3>
          <p className="mt-1 text-[12px] text-zinc-600">
            Biaya ditagih ke project Google kamu, bukan ke pemilik aplikasi.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="border border-zinc-300 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] hover:border-zinc-900"
        >
          Segarkan
        </button>
      </div>

      <dl className="grid grid-cols-2 divide-x divide-zinc-200 border-b border-zinc-200">
        <div className="p-4">
          <dt className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
            Total gambar
          </dt>
          <dd className="mt-1 font-mono text-3xl leading-none text-zinc-950" data-testid="usage-total-images">
            {usage.total_images}
          </dd>
        </div>
        <div className="p-4">
          <dt className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
            Estimasi biaya
          </dt>
          <dd className="mt-1 font-mono text-3xl leading-none text-zinc-950" data-testid="usage-total-cost">
            {money(usage.estimated_cost_usd)}
          </dd>
        </div>
      </dl>

      {!compact && (
        <dl className="grid grid-cols-2 divide-x divide-zinc-200 border-b border-zinc-200">
          <div className="p-4">
            <dt className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
              Bulan ini
            </dt>
            <dd className="mt-1 font-mono text-xl text-zinc-900">{usage.this_month_images} gambar</dd>
          </div>
          <div className="p-4">
            <dt className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
              Estimasi bulan ini
            </dt>
            <dd className="mt-1 font-mono text-xl text-zinc-900">
              {money(usage.this_month_estimated_cost_usd)}
            </dd>
          </div>
        </dl>
      )}

      {!compact && usage.by_month?.length > 1 && (
        <div className="border-b border-zinc-200 p-4">
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
            Riwayat per bulan
          </p>
          <ul className="mt-2 space-y-1">
            {usage.by_month
              .slice()
              .reverse()
              .map((row) => (
                <li key={row.month} className="flex justify-between text-[12px]">
                  <span className="text-zinc-600">{row.month}</span>
                  <span className="font-mono text-zinc-900">
                    {row.images} gambar ·{" "}
                    {money(row.cost_usd ?? row.images * (usage.price_table?.["1024x1024"] ?? 0.067))}
                  </span>
                </li>
              ))}
          </ul>
        </div>
      )}

      <div className="p-4">
        <p className="text-[11px] leading-relaxed text-zinc-500">
          Harga dasar {money(usage.price_table?.["1024x1024"] ?? 0.067)} per gambar
          (gemini-3.1-flash-image-preview, ukuran 1K).{" "}
          {usage.size_note}
        </p>
        <p className="mt-1.5 text-[11px] leading-relaxed text-zinc-500">
          Angka ini{" "}
          <strong className="font-medium text-zinc-700">perkiraan aplikasi</strong>, bukan
          tagihan resmi Google, jadi bisa berbeda sedikit karena pajak atau harga region.
          Gambar yang gagal dan tidak keluar tidak dihitung.
        </p>
        <a
          href={BILLING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-2 border border-zinc-900 px-3 py-1.5 text-[12px] hover:bg-zinc-950 hover:text-white"
        >
          Cek tagihan di AI Studio
          <span aria-hidden="true">&rarr;</span>
        </a>
      </div>
    </div>
  );
}
